import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Smartphone, Upload, Loader2, Power, PowerOff, Ban, Lock, Plus, Info } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { DashPanel } from '../components/DashPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { blogDb } from '@/lib/firebaseBlog';
import type { AppBuildMeta } from '../types/appBuild';
import {
  startAppBuildUpload,
  uploadAppBuildChunk,
  finishAppBuildUpload,
  listAppEditions,
  applyAppEditionAction,
  type AppEdition,
  type AppEditionAction,
} from './appBuildApi';

// Mesmo valor de BuildConfig.APP_EDITION no app Android (app/build.gradle.kts).
const DEFAULT_EDITION_ID = 'MCP_2026_A';

// Fatia bem abaixo do limite de ~1MiB por doc do Firestore (com folga pra
// virar base64 — infla ~33% — e pro resto do JSON do doc).
const CHUNK_SIZE_BYTES = 700_000;

function formatMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export default function AppBuildPage() {
  const { getIdToken } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [current, setCurrent] = useState<AppBuildMeta | null | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  // Controle remoto de disponibilidade, por edição (cada build do app tem seu próprio
  // BuildConfig.APP_EDITION — isso deixa controlar várias versões ao mesmo tempo). Lista
  // carregada 1x ao entrar na página; toda ação devolve o doc atualizado, sem reler.
  const [editions, setEditions] = useState<AppEdition[] | null>(null);
  const [editionsError, setEditionsError] = useState<string | null>(null);
  const [newEditionInput, setNewEditionInput] = useState('');
  const [addingEdition, setAddingEdition] = useState(false);
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ editionId: string; action: AppEditionAction } | null>(null);

  const loadEditions = useCallback(async () => {
    setEditionsError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      setEditions(await listAppEditions(token));
    } catch (err) {
      setEditionsError(err instanceof Error ? err.message : 'Falha ao carregar as edições');
    }
  }, [getIdToken]);

  useEffect(() => {
    loadEditions();
  }, [loadEditions]);

  const handleAddEdition = async () => {
    const id = newEditionInput.trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(id)) {
      setEditionsError('Identificador inválido. Use só letras, números, "_" ou "-".');
      return;
    }
    setAddingEdition(true);
    setEditionsError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      const result = await applyAppEditionAction(id, 'register', token);
      setEditions((prev) => [result, ...(prev ?? []).filter((e) => e.id !== result.id)]);
      setNewEditionInput('');
    } catch (err) {
      setEditionsError(err instanceof Error ? err.message : 'Falha ao cadastrar a edição');
    } finally {
      setAddingEdition(false);
    }
  };

  const requestAction = (editionId: string, action: AppEditionAction) => setConfirm({ editionId, action });

  const confirmAction = async () => {
    if (!confirm) return;
    const { editionId, action } = confirm;
    setConfirm(null);
    setActionPending(editionId);
    setEditionsError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      const result = await applyAppEditionAction(editionId, action, token);
      setEditions((prev) => (prev ?? []).map((e) => (e.id === editionId ? result : e)));
    } catch (err) {
      setEditionsError(err instanceof Error ? err.message : 'Falha ao atualizar a edição');
    } finally {
      setActionPending(null);
    }
  };

  const loadCurrent = useCallback(async () => {
    if (!blogDb) {
      setCurrent(null);
      return;
    }
    const snap = await getDoc(doc(blogDb, 'appBuilds', 'current'));
    setCurrent(snap.exists() ? (snap.data() as AppBuildMeta) : null);
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const handlePick = () => fileRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setProgress({ done: 0, total: 0 });
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');

      const chunkCount = Math.ceil(file.size / CHUNK_SIZE_BYTES);
      await startAppBuildUpload(
        {
          fileName: file.name,
          sizeBytes: file.size,
          contentType: file.type || 'application/vnd.android.package-archive',
          chunkCount,
        },
        token
      );

      for (let i = 0; i < chunkCount; i++) {
        const slice = file.slice(i * CHUNK_SIZE_BYTES, (i + 1) * CHUNK_SIZE_BYTES);
        const base64 = await blobToBase64(slice);
        await uploadAppBuildChunk(i, base64, token);
        setProgress({ done: i + 1, total: chunkCount });
      }

      await finishAppBuildUpload(token);
      setFile(null);
      await loadCurrent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar o app');
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardShell mode="app">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-foreground">Aplicativo</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            APK disponível pro botão "Baixar app" do site
          </p>
        </div>
        <UserMenu />
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <DashPanel className="space-y-4 max-w-xl">
          <div className="flex items-center gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              {current === undefined ? (
                <p className="text-sm text-muted-foreground">Carregando…</p>
              ) : current && current.status === 'ready' ? (
                <>
                  <p className="text-sm font-medium text-foreground truncate">{current.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatMB(current.sizeBytes)} · atualizado em{' '}
                    {new Date(current.updatedAt).toLocaleDateString('pt-BR')}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum app publicado ainda.</p>
              )}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <input ref={fileRef} type="file" accept=".apk" className="hidden" onChange={handleFileChange} />

          {!uploading ? (
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={handlePick} className="flex-1 justify-start">
                {file ? file.name : 'Escolher arquivo .apk'}
              </Button>
              <Button type="button" onClick={handleUpload} disabled={!file}>
                <Upload className="h-4 w-4" />
                Enviar
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Enviando fatia {progress.done} de {progress.total}…
              </p>
            </div>
          )}
        </DashPanel>

        <DashPanel className="space-y-4 max-w-xl">
          <div>
            <p className="text-sm font-semibold text-foreground">Disponibilidade da edição</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Liga e desliga o app remotamente, por versão — sem precisar de uma nova build.
            </p>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed space-y-1">
              <p>
                Cada identificador é uma edição (build) do app — o mesmo valor configurado em{' '}
                <code className="text-[11px]">BuildConfig.APP_EDITION</code>. Mantendo várias nessa lista, dá pra
                controlar a disponibilidade de cada versão de forma independente, ao mesmo tempo.
              </p>
              <p>
                <strong>Encerrar</strong> bloqueia o app dessa edição pra todo mundo, na próxima abertura — e pode
                ser revertido depois, reabrindo a edição.
              </p>
              <p>
                <strong>Encerrar permanentemente</strong> bloqueia e <u>não pode ser desfeito</u>: depois disso não
                existe botão nem forma de reativar essa edição por aqui. Use só quando tiver certeza que essa versão
                nunca mais vai ser usada.
              </p>
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <label htmlFor="new-edition-id" className="text-xs text-muted-foreground">Novo identificador</label>
              <Input
                id="new-edition-id"
                value={newEditionInput}
                onChange={(e) => setNewEditionInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddEdition();
                }}
                placeholder={`ex.: ${DEFAULT_EDITION_ID}`}
              />
            </div>
            <Button type="button" variant="outline" onClick={handleAddEdition} disabled={!newEditionInput.trim() || addingEdition}>
              {addingEdition ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Adicionar
            </Button>
          </div>

          {editionsError && <p className="text-sm text-destructive">{editionsError}</p>}

          <div className="divide-y divide-border">
            {editions === null ? (
              <p className="text-sm text-muted-foreground py-2">Carregando…</p>
            ) : editions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                Nenhuma edição cadastrada ainda. Adicione o identificador de uma build pra controlar a disponibilidade dela.
              </p>
            ) : (
              editions.map((ed) => (
                <div key={ed.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{ed.id}</p>
                    <p
                      className={
                        ed.permanentlyEnded
                          ? 'text-xs font-medium text-muted-foreground flex items-center gap-1'
                          : ed.enabled
                            ? 'text-xs font-medium text-emerald-600 dark:text-emerald-400'
                            : 'text-xs font-medium text-destructive'
                      }
                    >
                      {ed.permanentlyEnded && <Lock className="h-3 w-3" />}
                      {ed.permanentlyEnded ? 'Encerrada permanentemente' : ed.enabled ? 'Liberada' : 'Encerrada'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!ed.permanentlyEnded && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant={ed.enabled ? 'outline' : 'default'}
                          disabled={actionPending === ed.id}
                          onClick={() => requestAction(ed.id, ed.enabled ? 'end' : 'enable')}
                        >
                          {actionPending === ed.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : ed.enabled ? (
                            <PowerOff className="h-3.5 w-3.5" />
                          ) : (
                            <Power className="h-3.5 w-3.5" />
                          )}
                          {ed.enabled ? 'Encerrar' : 'Reabrir'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={actionPending === ed.id}
                          onClick={() => requestAction(ed.id, 'end_permanently')}
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Encerrar permanentemente
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DashPanel>
      </div>

      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === 'enable' && `Reabrir "${confirm.editionId}"?`}
              {confirm?.action === 'end' && `Encerrar "${confirm.editionId}"?`}
              {confirm?.action === 'end_permanently' && `Encerrar "${confirm.editionId}" permanentemente?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === 'enable' &&
                'O app dessa edição volta a funcionar normalmente pra quem abrir depois dessa mudança.'}
              {confirm?.action === 'end' &&
                'Quem abrir o app dessa edição vai ver a tela de teste encerrado. Você pode reabrir essa edição depois, quando quiser.'}
              {confirm?.action === 'end_permanently' &&
                'Essa ação não pode ser desfeita. Depois de confirmar, não existe nenhum jeito de reativar esta edição por aqui — use só se tiver certeza que essa versão nunca mais vai ser usada.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={confirm?.action !== 'enable' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={confirmAction}
            >
              {confirm?.action === 'enable' && 'Reabrir'}
              {confirm?.action === 'end' && 'Encerrar'}
              {confirm?.action === 'end_permanently' && 'Encerrar permanentemente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
